import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, HeartHandshake, PoundSterling } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import DonateBlock from "../components/DonateBlock";
import DotCluster from "../components/DotCluster";
import { LOGOS, IMG } from "../lib/content";
import { submitPartner, apiError } from "../lib/api";

const benefits = [
  { title: "Real community impact", body: "Your support directly funds youth provision, festivals and inclusion work with clear, evidenced outcomes." },
  { title: "Brand visibility", body: "Get your name in front of thousands across our events, festivals and channels throughout Cumbria." },
  { title: "Reinvested for good", body: "As a CIC, every penny of surplus goes back into our community objectives, not shareholders." },
];
const types = ["Headline sponsor", "Event sponsor", "In-kind / equipment", "Business / corporate", "Charity / third sector", "Individual supporter", "Other"];
const field = "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-base text-nead-navy placeholder:text-nead-navy/40 outline-none focus:border-nead-pink transition-colors";

export default function Partners() {
  const location = useLocation();
  const donateRef = useRef(null);
  const [form, setForm] = useState({ organisation: "", name: "", email: "", partnership_type: "Event sponsor", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (location.hash === "#donate") {
      setTimeout(() => donateRef.current?.scrollIntoView({ behavior: "smooth" }), 350);
    }
  }, [location.hash]);

  const scrollDonate = () => donateRef.current?.scrollIntoView({ behavior: "smooth" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitPartner(form);
      toast.success("Thank you. Our team will be in touch about sponsorship.");
      setForm({ organisation: "", name: "", email: "", partnership_type: "Event sponsor", message: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader label="Support Us" titleLines={["Sponsor our", "mission."]}
        intro="Your sponsorship and donations power everything we do, from Cumbria's first under-18s venue to our festivals and inclusion work. Support us as an organisation, or give what you can as an individual." />

      {/* Two ways to support */}
      <section className="container-x py-20 md:py-24">
        <Stagger className="grid md:grid-cols-2 gap-6">
          <StaggerItem>
            <div className="rounded-[1.5rem] border border-black/[0.07] p-8 h-full flex flex-col">
              <div className="flex h-12 w-12 items-center justify-center rounded-full grad-pink-cyan"><Handshake className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
              <h3 className="mt-6 font-display font-bold text-2xl text-nead-navy">Become a sponsor</h3>
              <p className="mt-3 text-nead-navy/60 leading-relaxed flex-1">Partner with us on a festival, project or year-round, with your brand front and centre and real social value to show for it.</p>
              <div className="mt-6"><a href="#sponsor-form" className="inline-flex rounded-full bg-nead-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-nead-pink transition-colors" data-testid="jump-sponsor">Enquire about sponsorship</a></div>
            </div>
          </StaggerItem>
          <StaggerItem>
            <div className="rounded-[1.5rem] border border-black/[0.07] p-8 h-full flex flex-col">
              <div className="flex h-12 w-12 items-center justify-center rounded-full grad-pink-cyan"><PoundSterling className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
              <h3 className="mt-6 font-display font-bold text-2xl text-nead-navy">Give a donation</h3>
              <p className="mt-3 text-nead-navy/60 leading-relaxed flex-1">Every contribution, large or small, helps us create opportunities for young people and stronger communities.</p>
              <div className="mt-6"><button onClick={scrollDonate} className="inline-flex rounded-full bg-nead-pink px-5 py-2.5 text-sm font-semibold text-white hover:bg-nead-navy transition-colors" data-testid="support-donate">Donate now</button></div>
            </div>
          </StaggerItem>
        </Stagger>
      </section>

      {/* Donate */}
      <section ref={donateRef} id="donate" className="bg-nead-mist scroll-mt-24">
        <div className="container-x py-24 md:py-32">
          <Reveal>
            <Eyebrow className="text-nead-pink">Make a donation</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy max-w-2xl">Give what you can.</h2>
          </Reveal>
          <div className="mt-12"><DonateBlock /></div>
        </div>
      </section>

      {/* Why sponsor */}
      <section className="bg-nead-ink text-white relative overflow-hidden">
        <DotCluster className="absolute top-0 right-0 h-96 w-96 opacity-50" count={60} />
        <div className="relative container-x py-24 md:py-32">
          <Reveal>
            <Eyebrow className="text-nead-cyan">Why support NEAD</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight max-w-2xl">Your support goes further.</h2>
          </Reveal>
          <Stagger className="mt-14 grid md:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <StaggerItem key={b.title}>
                <div className="rounded-[1.5rem] bg-white/5 border border-white/10 p-8 h-full">
                  <span className="font-display font-bold text-4xl text-gradient">0{i + 1}</span>
                  <h3 className="mt-4 font-display font-bold text-xl text-white">{b.title}</h3>
                  <p className="mt-3 text-white/60 leading-relaxed text-[0.95rem]">{b.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Sponsor enquiry */}
      <section id="sponsor-form" className="container-x py-24 md:py-32 scroll-mt-24">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <Eyebrow className="text-nead-pink">Become a sponsor</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy">Let's talk sponsorship.</h2>
            <p className="mt-6 text-nead-navy/60 text-lg leading-relaxed">Tell us a little about you and how you'd like to support NEAD, and our team will get back to you.</p>
            <p className="mt-4 text-nead-navy/70">
              Prefer email? Contact <span className="font-semibold text-nead-navy">Rocco Boyd</span> at{" "}
              <a href="mailto:rocco.boyd@nead.uk" className="font-semibold text-nead-pink hover:underline" data-testid="partner-email-link">rocco.boyd@nead.uk</a>.
            </p>
            <div className="mt-10 overflow-hidden rounded-[1.5rem] aspect-[16/10] hidden lg:block">
              <img src={IMG.boardroom} alt="Sponsorship meeting" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="space-y-5" data-testid="partner-form">
              <input required value={form.organisation} onChange={set("organisation")} placeholder="Organisation / your name" aria-label="Organisation" className={field} data-testid="partner-org" />
              <input required value={form.name} onChange={set("name")} placeholder="Contact name" aria-label="Name" className={field} data-testid="partner-name" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" aria-label="Email" className={field} data-testid="partner-email" />
              <select value={form.partnership_type} onChange={set("partnership_type")} aria-label="Sponsorship type" className={`${field} cursor-pointer`} data-testid="partner-type">
                {types.map((t) => <option key={t}>{t}</option>)}
              </select>
              <textarea required value={form.message} onChange={set("message")} placeholder="How would you like to support us?" aria-label="Message" rows={4} className={`${field} resize-none`} data-testid="partner-message" />
              <Button type="submit" disabled={busy} testId="partner-submit">{busy ? "Sending…" : "Send enquiry"}</Button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Supporters */}
      <section className="bg-nead-mist">
        <div className="container-x py-20 md:py-24">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Eyebrow className="text-nead-pink justify-center"><HeartHandshake className="h-4 w-4 mr-1 inline" />Our supporters</Eyebrow>
              <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy">With thanks to those who back us.</h2>
              <p className="mt-4 text-nead-navy/55">Proudly supported by organisations across Cumbria and beyond.</p>
            </div>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10" data-testid="supporter-logos">
            {LOGOS.map((src) => (
              <StaggerItem key={src}>
                <div className="group/logo relative flex h-16 items-center justify-center">
                  <div
                    aria-hidden
                    className="absolute inset-0 transition-opacity duration-300 group-hover/logo:opacity-0"
                    style={{
                      backgroundColor: "#1b2155",
                      WebkitMaskImage: `url(${src})`,
                      maskImage: `url(${src})`,
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                      WebkitMaskPosition: "center",
                      maskPosition: "center",
                      WebkitMaskSize: "contain",
                      maskSize: "contain",
                    }}
                  />
                  <img src={src} alt="" loading="lazy" className="relative max-h-16 max-w-full object-contain opacity-0 transition-opacity duration-300 group-hover/logo:opacity-100" />
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
